import classnames from 'classnames';

/**
 * Utility function to calculate position of a DOM element.
 * @param {element} DOM element
 * @return {x, y} absolute position of element
 */
export function getElementOffset(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left,
    y: rect.top,
  };
}

/**
 * Open a file link
 * @param {url} file link
 * @param {filename} suggested filename for browser to use
 */
export function openFile(url: string, filename?: string | void) {
  const anchor = document.createElement('a');
  const clickEvent = new MouseEvent('click', {
    bubbles: true,
    cancelable: false,
    view: window,
  });

  anchor.href = url;
  if (filename) {
    anchor.download = filename;
  }
  anchor.dispatchEvent(clickEvent);
}

/**
 * Generate suffixed CSS class names for reusable react components
 * @param {baseClassNames} semantic oriented base CSS class names used in the component
 * @param {suffix} indicating the context of reuse with different graphic arrangement
 * @return {suffixedClassnames} CSS class names to be used in reusable react component
 */
export function suffixedClassnames(
  baseClassNames: { [key: string]: string },
  suffix: string,
): { [key: string]: string } {
  const suffixedNames: { [key: string]: string } = {};

  Object.keys(baseClassNames).forEach((k) => {
    suffixedNames[k] = classnames(baseClassNames[k], {
      [`${baseClassNames[k]}-at-${suffix}`]: suffix,
    });
  });

  return suffixedNames;
}

/**
 * Deep compare two objects for equality
 */
export const deepEqual = function (x: any, y: any): boolean {
  if (x === y) {
    return true;
  }

  if (
    typeof x === 'object' &&
    x != null &&
    typeof y === 'object' &&
    y != null
  ) {
    if (Object.keys(x).length !== Object.keys(y).length) {
      return false;
    }

    for (const prop in x) {
      if (Object.prototype.hasOwnProperty.call(y, prop)) {
        if (!deepEqual(x[prop], y[prop])) {
          return false;
        }
      } else {
        return false;
      }
    }

    return true;
  }
  return false;
};

export function formatBytes(bytes: number) {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const kilo = 1000;
  const dm = 1;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(kilo));
  return `${parseFloat((bytes / Math.pow(kilo, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format numeric fields to display in ".01" format, e.g. for currency
 * @param {v} number
 * @return {v} formatted string
 */
export function formatCurrency(v: number) {
  return v.toFixed(2).toString();
}

export function isEmpty(s: string) {
  if (s !== null && s.trim().length > 0) {
    return false;
  }

  return true;
}

/**
 * Decode a HTML string
 * @param input string to be decoded
 */
export function htmlDecode(input: string): string {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
}

/**
 * Joins a list of items, to be a comma separated string
 * Automatically filters out nullish values
 * @param items list of items to join
 * @param joiner string to join with, default to a comma
 */
export function joinForDisplay(
  items: (string | void)[],
  joiner = ', ',
): string {
  return items.filter((x) => !!x).join(joiner);
}

export function isNullish(value: any) {
  return value === undefined || value === null;
}

export function pluralise(num: number, word: string) {
  return num === 1 ? `${num} ${word}` : `${num} ${word}s`;
}

export function pluraliseWithOutNum(num: number, word: string) {
  return num === 1 ? `${word}` : `${word}s`;
}

export function isOrAre(num: number) {
  return num === 1 ? `is` : `are`;
}

export function hasOrHave(num: number) {
  return num === 1 ? `has` : `have`;
}

export function urlLeadingSlashFormatter(url: string) {
  if (!url.startsWith('/')) {
    return '/' + url;
  }
  return url;
}

export function rtrim(x: string, characters: string) {
  let end = x.length - 1;
  while (characters.indexOf(x[end]) >= 0) {
    end -= 1;
  }
  return x.substring(0, end + 1);
}

export function ltrim(x: string, characters: string) {
  let start = 0;
  while (characters.indexOf(x[start]) >= 0) {
    start += 1;
  }
  return x.substring(start);
}

export function urlJoin(leftUrl: string, rightURL: string) {
  const strippedLeftUrl = rtrim(leftUrl, '/');
  const strippedRightURL = ltrim(rightURL, '/');
  return `${strippedLeftUrl}/${strippedRightURL}`;
}
